import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { useUpdateTimeEntryMutation } from '@/store/api/apiSlice';
import Button from '@/components/common/Button';
import Input from '@/components/common/Input';
import { getApiError } from '@/utils/errorUtils';
import { entryToHours } from '@/utils/timesheet';

type Props = {
  open: boolean;
  onClose: () => void;
  entry: any | null;
  onSaved?: () => void;
};

export default function EditTimeEntryModal({ open, onClose, entry, onSaved }: Props) {
  const [date, setDate] = useState('');
  const [hours, setHours] = useState('1');
  const [description, setDescription] = useState('');
  const [updateEntry, { isLoading }] = useUpdateTimeEntryMutation();

  useEffect(() => {
    if (!open || !entry) return;
    setDate(format(new Date(entry.startTime), 'yyyy-MM-dd'));
    setHours(String(Math.max(0.25, Math.round(entryToHours(entry) * 4) / 4)));
    setDescription(entry.description || '');
  }, [open, entry]);

  if (!open || !entry) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const h = Number(hours);
    if (!h || h <= 0 || h > 24) {
      toast.error('Enter valid hours (0–24)');
      return;
    }
    const start = new Date(`${date}T09:00:00`);
    const end = new Date(start.getTime() + h * 3600000);
    try {
      await updateEntry({
        id: entry.id,
        startTime: start.toISOString(),
        endTime: end.toISOString(),
        description: description.trim() || undefined,
      }).unwrap();
      toast.success('Entry updated');
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to update entry'));
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5">
        <h2 className="text-base font-bold text-text-primary">Edit time entry</h2>
        <p className="text-xs text-text-muted mt-1 truncate">{entry.task?.title || 'Task'}</p>
        <form className="mt-4 space-y-3" onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Date" type="date" value={date} onChange={(e) => setDate(e.target.value)} fullWidth />
            <Input
              label="Hours"
              type="number"
              step="0.25"
              min="0.25"
              max="24"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              fullWidth
            />
          </div>
          <Input label="Note" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth />
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" isLoading={isLoading}>Save changes</Button>
          </div>
        </form>
      </div>
    </div>
  );
}
